import React from "react";
import { ArrowRight, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { money, num, ago } from "@/lib/sk";
import { ValidationBadge } from "@/components/canvas/Badges";
import { CONFIG_STATUS, levels, ruleLabel } from "./model";

const M = (v) => (v == null ? "–" : money(v, "NOK", 1, { bare: true }));

/** Where the configuration stands, in one line – and why it is not simply «green». */
export function StatusLine({ active, stats, onValves }) {
  const s = CONFIG_STATUS[active.validation?.status] || CONFIG_STATUS.validated;
  const v = active.validation || {};
  const unsized = v.unsized_valves || 0;
  return (
    <div className="mt-5 flex flex-wrap items-start justify-between gap-3" data-testid="konfig-status" data-status={active.validation?.status}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2"><span className={cn("v-badge", s.cls)} data-testid="konfig-status-badge">{s.label}</span><span className="k-meta">{active.name} · kjørt {ago(active.created_at)} av {active.created_by?.name || "systemet"} · satsversjon v{active.settings_version}</span></div>
        <p className="k-body mt-1.5 text-foreground/80">{s.lead} <strong className="font-medium">{v.sized_valves} av {(v.sized_valves || 0) + unsized} ventiler</strong> er dimensjonert{unsized ? <> – <button type="button" onClick={onValves} className="text-[hsl(var(--brand-strong))] underline-offset-[3px] hover:underline" data-testid="konfig-unsized-link">{unsized} må avklares i ventillisten</button></> : null}.</p>
        {active.outdated?.length ? <p className="mt-1.5 text-[13.5px] font-medium text-[hsl(var(--v-assumed))]" data-testid="konfig-outdated">Utdatert: {active.outdated.join("; ")}.</p> : null}
      </div>
    </div>
  );
}

/** Three figures that are deliberately not one: what the parts cost · the configuration's list price · the calculation. */
export function Levels({ active, totals }) {
  return (
    <div className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-3" data-testid="konfig-levels">
      {levels(active, totals).map((l, i) => (
        <div key={l.key} className={cn("min-w-0", i > 0 && "sm:border-l sm:border-black/[0.06] sm:pl-8")} data-testid={`konfig-level-${l.key}`}>
          <div className="k-label">{l.label}</div>
          <div className="k-figure mt-1 tabular">{M(l.value)}{l.preliminary && l.value != null ? <span className="ml-2 align-middle text-[12px] font-medium text-[hsl(var(--v-assumed))]">foreløpig</span> : null}</div>
          <div className="k-meta mt-0.5">{l.hint}{l.key === "kalkyle" && l.db != null ? ` · DB ${num(l.db, 1)} %` : ""}{l.key === "liste" && active?.totals?.gm_pct != null ? ` · margin ${num(active.totals.gm_pct, 1)} %` : ""}</div>
        </div>
      ))}
    </div>
  );
}

/** The bridge: is the calculation in step with the configuration, and what will change if you lay it in. */
export function SyncLine({ view, ctx, k, onKalkyle }) {
  const a = view.active;
  const s = view.sync || {};
  const stale = ctx?.konfig?.stale_overrides || 0;
  const kinds = [s.technical ? `${s.technical} teknisk endret (utførelse/stykkliste)` : null, s.commercial ? `${s.commercial} kostgrunnlag oppdatert` : null].filter(Boolean);
  const parts = [s.counts?.added ? `${s.counts.added} nye linjer` : null, s.counts?.changed ? `${s.counts.changed} endres${kinds.length ? ` (${kinds.join(", ")})` : ""}` : null, s.counts?.removed ? `${s.counts.removed} fjernes` : null, s.counts?.stale ? `${s.counts.stale} overstyringer må ses på` : null].filter(Boolean);
  const inSync = s.in_sync && a.synced;
  return (
    <div className={cn("mt-6 flex flex-wrap items-center justify-between gap-3 rounded-[14px] px-4 py-3", inSync ? "bg-black/[0.03]" : "bg-[hsl(var(--brand-soft))]")} data-testid="konfig-sync" data-in-sync={inSync ? "1" : "0"}>
      <div className="min-w-0 text-[14px]">
        {inSync ? <>Kalkylen er i takt med konfigurasjonen{a.synced ? <span className="text-muted-foreground"> · lagt inn {ago(a.synced.at)} av {a.synced.by}</span> : null}.</>
          : <><strong className="font-medium">Kalkylen er ikke oppdatert.</strong> {parts.length ? parts.join(" · ") + "." : "Konfigurasjonen er ikke lagt i kalkylen ennå."}</>}
        {stale ? <> <button type="button" onClick={onKalkyle} className="font-medium text-[hsl(var(--v-assumed))] underline-offset-[3px] hover:underline" data-testid="konfig-stale-link">{stale} manuelle {stale === 1 ? "overstyring" : "overstyringer"} er utdatert – se Kalkyle</button></> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {inSync ? <button type="button" onClick={onKalkyle} className="inline-flex h-8 items-center gap-1 rounded-full px-3 text-[13.5px] font-medium text-[hsl(var(--brand-strong))] hover:bg-black/[0.05]" data-testid="konfig-open-kalkyle">Åpne kalkylen <ArrowRight className="h-3.5 w-3.5" /></button>
          : <button type="button" disabled={k.busy === "sync"} onClick={() => k.sync(a.id)} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[hsl(var(--brand))] px-3.5 text-[13.5px] font-semibold text-white hover:bg-[hsl(var(--brand-strong))] disabled:opacity-50" data-testid="konfig-sync-button"><RefreshCw className={cn("h-3.5 w-3.5", k.busy === "sync" && "animate-spin")} /> Legg i kalkylen</button>}
      </div>
    </div>
  );
}

/** Technical deviations from the active configuration – identical messages are folded into one line with the tags, so 48 valves with the same cause read as one cause. */
export function Issues({ issues, onValve }) {
  if (!issues?.length) return null;
  const grouped = [];
  for (const i of issues) {
    const msg = String(i.message || "").replace(/^[^:]{3,40}:\s/, "");
    const hit = grouped.find((g) => g.msg === msg && g.level === i.level);
    if (hit) hit.tags.push(...(i.tags || []));
    else grouped.push({ msg, level: i.level, tags: [...(i.tags || [])] });
  }
  return (
    <section className="mt-6" data-testid="konfig-issues" data-count={issues.length}>
      <div className="k-label mb-2">Tekniske avvik ({issues.length}{grouped.length !== issues.length ? ` · ${grouped.length} årsaker` : ""})</div>
      <ul className="divide-y divide-black/[0.04] rounded-[14px] border border-black/[0.06]">
        {grouped.map((g, n) => (
          <li key={n} className="flex items-start gap-3 px-3.5 py-2.5 text-[13.5px]" data-testid={`konfig-issue-${n}`} data-tags={g.tags.length}>
            <span className={cn("mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full", g.level === "blocked" ? "bg-[hsl(var(--v-blocked))]" : "bg-[hsl(var(--v-eng))]")} />
            <div className="min-w-0 flex-1">
              <div>{g.msg}</div>
              {g.tags.length ? <div className="k-meta mt-0.5 truncate" title={g.tags.join(", ")}>{g.tags.length === 1 ? g.tags[0] : `${g.tags.length} ventiler: ${g.tags.slice(0, 4).join(", ")}${g.tags.length > 4 ? ` +${g.tags.length - 4}` : ""}`}</div> : null}
            </div>
            {g.tags.length ? <button type="button" onClick={() => onValve(g.tags[0])} className="shrink-0 text-[13px] font-medium text-[hsl(var(--brand-strong))] underline-offset-[3px] hover:underline" data-testid={`konfig-issue-open-${n}`}>Åpne {g.tags.length === 1 ? g.tags[0] : "første"}</button> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The configured delivery, group by group: every component with quantity, cost, list price, rule and validation. */
export function Groups({ active, selected, onComponent }) {
  return (
    <section className="mt-8" data-testid="konfig-groups">
      {(active.groups_full || []).map((g) => {
        const sum = active.groups?.find((x) => x.name === g.name) || {};
        return (
          <div key={g.id || g.name} className="mt-6 first:mt-0" data-testid={`konfig-group-${g.name}`}>
            <div className="flex items-baseline justify-between gap-3 border-b border-black/[0.08] pb-1.5">
              <div className="flex items-baseline gap-2"><span className="k-ref">{sum.ref}</span><h3 className="k-title font-semibold">{g.name}</h3><span className="k-meta">{g.items.length} {g.items.length === 1 ? "komponent" : "komponenter"}</span></div>
              <div className="tabular flex items-baseline gap-4 text-[13.5px]"><span className="text-muted-foreground">Kost {M(sum.cost)}</span><span className="w-[110px] text-right">{M(sum.price)}</span></div>
            </div>
            <ul>
              {g.items.map((it) => (
                <li key={it.component_key}>
                  <button type="button" onClick={() => onComponent(it.component_key)} className={cn("k-row grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 rounded-[10px] px-2 text-left transition-colors hover:bg-black/[0.025] sm:grid-cols-[minmax(0,1fr)_minmax(140px,auto)_110px_auto]", selected === it.component_key && "bg-[hsl(var(--brand)/0.05)]")} data-testid={`konfig-comp-${it.component_key}`} data-validation={it.validation}>
                    <div className="min-w-0"><div className="truncate text-[14.5px] font-medium">{num(it.qty, Number.isInteger(it.qty) ? 0 : 1)} {it.unit} · {it.name}</div><div className="k-meta truncate">{ruleLabel(it.rule)}{it.for_valves?.length ? ` · ${it.for_valves.length} ventiler` : ""}</div></div>
                    <div className="tabular hidden text-right text-[13.5px] text-muted-foreground sm:block">{M(it.cost_price)} × {num(it.qty)}</div>
                    <div className="tabular hidden text-right text-[14px] sm:block">{M(it.qty * it.sales_price)}</div>
                    <div className="flex justify-end"><ValidationBadge state={it.validation} short testId={`konfig-comp-validation-${it.component_key}`} /></div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
}
